import {
  AttachFile,
  Bookmark,
  Label,
  MoreVert,
  Person,
  Send,
  ShoppingBag,
} from '@mui/icons-material'
import { message as messageAntd } from 'antd'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import {
  Avatar,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Popover,
  TextField,
  Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { IMessageItem } from '../../../resources/chats'
import ChatMessages from './ChatMessages'
import {
  EFacebookChatRoomStatus,
  IFacebookChatRoom,
  IFacebookChatRoomTag,
} from '../../../types/facebook'
import useAxios, { axiosInstance } from '../../../hooks/useAxios'
import { isEmpty } from 'radash'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import dayjs from 'dayjs'
import TagFormModal from './TagFormModal'
import UserSelectSearch from '../../../components/SelectSearch/UserSelectSearch'
import { message } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import {
  chatRoomMutateState,
  updateChantRoomById,
  updateChatRoomsAssingToUser,
} from '../../../../store/reducers/facebook/facebook-chat.slice'
import { FiEye, FiEyeOff, FiShoppingBag, FiTrash2 } from 'react-icons/fi'
import ChatCustomerPanel from './ChatCustomerPanel'
import { useUser } from '../../../hooks/useUser'
import { isRoleMerchant } from '../../../utils/getRoleUser'
import ActorPopover from './ChatActorPopup'

interface ChatWindowProps {
  isOpenedCustomerPanel: boolean
  setIsOpenedCustomerPanel: React.Dispatch<React.SetStateAction<boolean>>
  chatRoom?: IFacebookChatRoom | null
  incommingMessage?: IMessageItem
  handleMutate: () => void
}

const ChatWindow = (props: ChatWindowProps) => {
  const {
    isOpenedCustomerPanel,
    setIsOpenedCustomerPanel,
    incommingMessage,
    handleMutate,
  } = props

  const chatRoomMutate = useSelector(chatRoomMutateState)

  const { data: chatRoom, mutate } = useAxios<IFacebookChatRoom>({
    method: 'GET',
    url: `/facebook/pages/chatroom/${props.chatRoom?.objectId}`,
  })

  const [message, setMessage] = useState<string>('')
  const [paging, setPaging] = useState<string>('')
  const [isLoading, setIsLoading] = useState<{
    loading: boolean
    firstLoad: boolean
  }>({
    loading: true,
    firstLoad: true,
  })
  const { user } = useUser(false)
  const isMerchant = isRoleMerchant(user?.roles ?? [])
  const [isOpenedTagModal, setIsOpenedTagModal] = useState<boolean>(false)

  const [tags, setTags] = useState<IFacebookChatRoomTag[]>([])
  const [assignToUser, setAssignToUser] = useState<string | null>(
    chatRoom?.assignToUser ?? null,
  )
  const [sending, setSending] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [messages, setMessages] = useState<IMessageItem[]>([])
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null)

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setPopoverAnchor(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setPopoverAnchor(null)
  }

  const isPopoverOpen = Boolean(popoverAnchor)

  const fileInputRef = useRef<any>(null)
  const messageInputRef = useRef<any>(null)

  useEffect(() => {
    if (incommingMessage && incommingMessage.from.id === chatRoom?.userId) {
      setMessages([...messages, incommingMessage])
    }
  }, [incommingMessage])

  useEffect(() => {
    if (chatRoom) {
      setPaging('')
      setIsLoading({
        loading: true,
        firstLoad: true,
      })
      handleFeatchMessage()
      setTags(chatRoom.tags)
      setAssignToUser(chatRoom.assignToUser ?? null)
      chatRoomMutate?.()
      handleMutate?.()
    }
  }, [chatRoom])

  const handleFeatchMessage = useCallback(
    async (paging: string = '', firstLoading: boolean = true) => {
      setIsLoading({
        ...isLoading,
        loading: true,
      })
      const params: any = {
        userId: chatRoom?.userId,
        conversationId: chatRoom?.chatId,
      }
      if (paging) {
        params.paging = paging
      }

      if (!firstLoading && paging === '') {
        setPaging('')
        return
      }
      const response = await axiosInstance.request({
        url: `/facebook/pages/conversations/${chatRoom?.pageId}/messages`,
        method: 'GET',
        params,
      })

      const data = response.data
      handleSetMessage(data, firstLoading)
      setPaging(data?.paging)
    },
    [chatRoom, paging, isLoading],
  )

  const handleSetMessage = (data: any, firstLoading: boolean = true) => {
    const items: IMessageItem[] =
      data?.messages?.map((message: any) => ({
        id: message.id,
        message: message.message,
        from: {
          id: message?.from?.id ?? '-',
          name: message?.from?.name ?? '-',
        },
        created_time: message.created_time,
        attachments: message.attachments ?? [],
        sticker: message?.sticker ?? '',
      })) ?? []
    setMessages((prev) => (firstLoading ? items : [...items, ...prev]))
    setIsLoading({
      ...isLoading,
      loading: false,
    })
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSetFirstLoad = (status: boolean) => {
    setIsLoading({
      ...isLoading,
      firstLoad: status,
    })
  }

  const handlePaging = () => {
    if (isLoading.loading) {
      return
    }
    setTimeout(() => {
      handleFeatchMessage(paging, false)
    }, 1000)
  }

  const handleOnUpload = async (event: any) => {
    const file = event.target.files[0]
    if (file) {
      setSending(true)
      const formData = new FormData()
      formData.append('file', file)
      try {
        const response = await axiosInstance.post<any>('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        if (response.status !== 201) {
          return messageAntd.error('อัพโหลดรูปภาพไม่สำเร็จ')
        }
        await handleOnSendMessage(response.data.url, 'image')
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        messageAntd.error(messageError)
        setSending(false)
      }
    }
  }

  const handleOnSendMessage = useCallback(
    async (instanstMessage?: string, type?: string) => {
      setSending(true)
      try {
        await axiosInstance.request({
          url: `/facebook/pages/conversations/messages`,
          method: 'POST',
          data: {
            pageId: chatRoom?.pageId,
            message: instanstMessage ?? message,
            recipient: chatRoom?.userId,
            type: type ? type : 'message',
          },
        })
        let attachments: any = []
        if (type === 'image') {
          attachments = {
            data: [{ name: 'image', image_data: { url: instanstMessage } }],
          }
        }
        setMessages([
          ...messages,
          {
            id: dayjs().unix().toString(),
            message,
            from: {
              id: chatRoom?.pageId ?? '-',
            },
            created_time: new Date().toISOString(),
            attachments,
          },
        ])
        setMessage('')
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'ไม่สามารถส่งข้อความได้'
        messageAntd.error(messageError)
      }
      setSending(false)

      if (messageInputRef?.current) {
        setTimeout(() => {
          messageInputRef.current.focus()
        }, 1000)
      }
    },
    [chatRoom, messages, message, sending],
  )

  const handleUpdateStatus = useCallback(
    async (status: string) => {
      try {
        await axiosInstance.put(
          `/facebook/pages/chatroom/${chatRoom?.objectId}/status`,
          {
            status,
          },
        )
        mutate()
        chatRoomMutate?.()
        messageAntd.success('อัพเดทสถานะสำเร็จ')
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        messageAntd.error(messageError)
      }
    },
    [chatRoom],
  )

  const open = Boolean(anchorEl)

  if (!chatRoom || (isLoading.loading && isLoading.firstLoad)) {
    return <></>
  }

  return (
    <Grid container>
      <Grid
        item
        xs={isOpenedCustomerPanel ? 8 : 12}
        sx={{
          borderRight: 1,
          borderColor: 'grey.200',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            width: '100%',
            position: 'relative',
          }}
        >
          <ActorPopover
            setAssignToUser={setAssignToUser}
            ids={[chatRoom.objectId]}
            anchorEl={popoverAnchor}
            isOpen={isPopoverOpen}
            onClose={handlePopoverClose}
            onAssignTo={(assignTo: string) => {
              console.log('Assign to', assignTo)
            }}
          />
          <CustomPopover
            chatRoom={chatRoom}
            handleUpdateStatus={handleUpdateStatus}
            anchorEl={anchorEl}
            isOpen={open}
            onClose={handleClose}
          />
          <TagFormModal
            setTags={setTags}
            id={chatRoom.objectId}
            open={isOpenedTagModal}
            onClose={() => setIsOpenedTagModal(false)}
            handleMutate={() => mutate}
          />
          <IconButton
            onClick={() => setIsOpenedCustomerPanel(!isOpenedCustomerPanel)}
            sx={{
              backgroundColor: '#ffffff',
              width: isOpenedCustomerPanel ? 40 : 80,
              height: 50,
              borderRadius: '30px 0 0 30px',
              position: 'absolute',
              bottom: '500px',
              right: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 1,
              borderColor: 'grey.300',
              zIndex: 999,
              '&:hover': {
                backgroundColor: '#fff',
              },
            }}
          >
            {isOpenedCustomerPanel ? (
              <RemoveCircleOutlineIcon />
            ) : (
              <>
                <AddCircleOutlineIcon />
                <Box sx={{ marginLeft: 1 }}>
                  <Typography variant="subtitle2">ข้อมูล</Typography>
                </Box>
              </>
            )}
          </IconButton>
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                pl: 5,
                pt: 2,
                pb: 1,
              }}
            >
              <Avatar
                src={chatRoom.imageUrl}
                alt={chatRoom.name}
                sx={{ width: 52, height: 52, mr: 2 }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" mb={0.5} noWrap fontWeight="bold">
                  {chatRoom.name}
                </Typography>
                <Typography
                  variant="body2"
                  lineHeight={1.2}
                  color="textSecondary"
                >
                  {chatRoom.pageName}
                </Typography>
                <Typography variant="body2" fontSize={12} color="textSecondary">
                  ผู้ดูแล: {assignToUser || 'ไม่ระบุ'}
                </Typography>
              </Box>
              <IconButton onClick={handleClick}>
                <MoreVert />
              </IconButton>
              <Box
                sx={{ display: 'flex', gap: 1, alignItems: 'center', pr: 2 }}
              >
                <IconButton
                  onClick={() =>
                    handleUpdateStatus(EFacebookChatRoomStatus.HOLD)
                  }
                >
                  <Bookmark
                    color={
                      chatRoom?.status === EFacebookChatRoomStatus.HOLD
                        ? 'warning'
                        : 'inherit'
                    }
                  />
                </IconButton>

                <Button
                  color={
                    chatRoom?.status === EFacebookChatRoomStatus.OPEN
                      ? 'error'
                      : 'success'
                  }
                  variant="outlined"
                  onClick={() =>
                    handleUpdateStatus(
                      chatRoom?.status === EFacebookChatRoomStatus.OPEN
                        ? EFacebookChatRoomStatus.CLOSED
                        : EFacebookChatRoomStatus.OPEN,
                    )
                  }
                  startIcon={
                    chatRoom?.status === EFacebookChatRoomStatus.OPEN ? (
                      <FiEyeOff />
                    ) : (
                      <FiEye />
                    )
                  }
                >
                  {chatRoom?.status === EFacebookChatRoomStatus.OPEN
                    ? 'ปิดแชท'
                    : 'เปิดแชท'}
                </Button>
                {/* {isMerchant && (
                  <Button
                    variant="outlined"
                    startIcon={<Person />}
                    onClick={handleFilterClick}
                  >
                    ผู้ดูแล
                  </Button>
                )} */}
              </Box>
            </Box>

            <Box
              sx={{
                px: 4,
                pb: 1,
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'background.paper',
                height: 70,
                gap: 1,
              }}
            >
              <Button
                variant="outlined"
                sx={{ minWidth: 150 }}
                size="small"
                startIcon={<Label sx={{ color: 'primary.main' }} />}
                onClick={() => setIsOpenedTagModal(true)}
              >
                เพิ่มแท็ก ({tags?.length})
              </Button>
              {tags?.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 0.5,
                  }}
                >
                  {tags.map((tag) => {
                    return (
                      <Tag
                        key={tag.name}
                        id={chatRoom.objectId}
                        mutate={mutate}
                        name={tag.name}
                        color={tag.color}
                      />
                    )
                  })}
                </Box>
              )}
            </Box>
          </Box>
          <Divider />

          <ChatMessages
            handleSetFirstLoad={handleSetFirstLoad}
            firstLoad={isLoading.firstLoad}
            isLoading={isLoading.loading}
            image={chatRoom.imageUrl}
            messageListData={messages}
            onHandleMessageList={() => handlePaging()}
            messagePaging={!!paging}
            isLoadingMessage={false}
            chat={{
              pageId: chatRoom.pageId,
              name: chatRoom.pageName,
            }}
            setMessageOrderDetail={() =>
              console.log('Set message order detail')
            }
            setIsOpenedOrderDetailModal={() =>
              console.log('Set is opened order detail modal')
            }
          />

          <Divider />

          <Box
            sx={{
              p: 2,
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <TextField
              ref={messageInputRef}
              variant="outlined"
              placeholder="พิมพ์ข้อความหรือคีย์เวิร์ด..."
              fullWidth
              multiline
              aria-readonly={sending}
              //   disabled={sending}
              rows={3}
              value={message}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleOnSendMessage()
                }
              }}
              onChange={(e) => setMessage(e.target.value)}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
              }}
            >
              <Box>
                {/* <IconButton>
              <ChatBubbleOutline />
            </IconButton> */}
                <IconButton
                  disabled={sending}
                  onClick={() => {
                    if (fileInputRef?.current) {
                      fileInputRef?.current?.click?.()
                    }
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleOnUpload}
                    style={{
                      opacity: 0,
                      position: 'absolute',
                      zIndex: -1,
                      width: '100%',
                      height: '100%',
                    }}
                  />
                  <AttachFile />
                </IconButton>
                {/* <IconButton>
              <EmojiEmotions />
            </IconButton> */}
                <IconButton
                  onClick={() =>
                    setIsOpenedCustomerPanel(!isOpenedCustomerPanel)
                  }
                >
                  <ShoppingBag />
                </IconButton>
              </Box>
              <IconButton
                disabled={sending || isEmpty(message)}
                onClick={() => handleOnSendMessage()}
                color="primary"
              >
                <Send />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Grid>
      <Grid item lg={isOpenedCustomerPanel ? 4 : 0}>
        <ChatCustomerPanel
          mutate={mutate}
          chatRoom={chatRoom}
          isOpenedCustomerPanel={isOpenedCustomerPanel}
        />
      </Grid>
    </Grid>
  )
}

interface CustomPopoverProps {
  anchorEl: HTMLElement | null
  isOpen: boolean
  onClose: () => void
  handleUpdateStatus: (status: string) => void
  chatRoom: IFacebookChatRoom | null
}

const CustomPopover: React.FC<CustomPopoverProps> = ({
  anchorEl,
  isOpen,
  onClose,
  handleUpdateStatus,
  chatRoom,
}) => {
  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      sx={{
        zIndex: 50,
        minWidth: 'fit-content',
        '--radix-popper-transform-origin': '50% 0px',
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        p={0}
        width="fit-content"
        className="shadow-md bg-white rounded-md"
      >
        <Button
          startIcon={<FiEye fontSize={18} />}
          sx={{
            justifyContent: 'flex-start',
            color: 'text.primary',
            py: 1.25,
            px: 2,
            minWidth: '28px',
            '&:hover': { backgroundColor: 'grey.100' },
          }}
          onClick={() => handleUpdateStatus(EFacebookChatRoomStatus.OPEN)}
        >
          <Typography variant="body2">เปิดแชต</Typography>
        </Button>
        <Button
          startIcon={<FiEyeOff fontSize={18} />}
          sx={{
            justifyContent: 'flex-start',
            color: 'text.primary',
            py: 1.25,
            px: 2,
            minWidth: '28px',
            '&:hover': { backgroundColor: 'grey.100' },
          }}
          onClick={() => handleUpdateStatus(EFacebookChatRoomStatus.CLOSED)}
        >
          <Typography variant="body2">ปิดแชต</Typography>
        </Button>
        <Button
          startIcon={<FiShoppingBag fontSize={18} />}
          sx={{
            justifyContent: 'flex-start',
            color: 'text.primary',
            py: 1.25,
            px: 2,
            minWidth: '28px',
            '&:hover': { backgroundColor: 'grey.100' },
          }}
          onClick={() => handleUpdateStatus(EFacebookChatRoomStatus.PURCHASED)}
        >
          <Typography variant="body2">ชำระเงินแล้ว</Typography>
        </Button>
      </Box>
    </Popover>
  )
}

const Tag = ({
  name,
  color,
  id,
  mutate,
}: {
  name: string
  color: string
  id: string
  mutate: () => void
}) => {
  const handleOnDelete = async () => {
    try {
      await axiosInstance.delete(`/facebook/pages/chatrooms/${id}/tags`, {
        data: { tag: name },
      })
      mutate()
      message.success('ลบแท็กสำเร็จ')
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  return (
    <Box
      sx={{
        px: 2,
        py: 0.5,
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 2,
        bgcolor: color,
        color: '#fff',
        fontSize: '10',
        fontWeight: '400',
        gap: 1,
      }}
    >
      <Typography fontSize={12}>{name}</Typography>
      <IconButton onClick={() => handleOnDelete()} size="small">
        <FiTrash2 color="#fff" fontSize="11" />
      </IconButton>
    </Box>
  )
}

export default ChatWindow
